import { useState, useEffect } from "react";
import { Loader2, RefreshCcw, ChevronDown } from "lucide-react";
import axiosInstance from "../api/axiosInstance";
import { toast } from "react-toastify";

const Test = () => {
  const [url, setUrl] = useState("");
  const [customInstructions, setCustomInstructions] = useState("");
  const [loading, setLoading] = useState(false);
  const [htmlCaptures, setHtmlCaptures] = useState([]);
  const [fetchingCaptures, setFetchingCaptures] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Create new replica ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return toast.error("Please enter a website URL.");

    setLoading(true);
    setError(null);

    try {
      const { data } = await axiosInstance.post("api/webhooks/create-html-raplica", {
        url: url.trim(),
        customInstructions: customInstructions.trim() || undefined,
      });

      if (data?.success) {
        toast.success("HTML replica created successfully!");
        setUrl("");
        setCustomInstructions("");
        fetchCaptures();
      } else {
        throw new Error(data?.message || "Failed to create replica");
      }
    } catch (err) {
      console.error("Create request failed:", err);
      toast.error(err.message || "Something went wrong");
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Fetch replicas ---
  const fetchCaptures = async (isInitial = false) => {
    try {
      if (isInitial) setInitialLoading(true);
      setFetchingCaptures(true);
      setError(null);

      const { data } = await axiosInstance.post("api/webhooks/get-html-raplica");

      if (!data?.success) throw new Error(data?.message || "Failed to fetch replicas");

      const replicas = (data?.data || [])
        .map((item) => ({
          ...item,
          openDetails: false,
          date: new Date(item.updated_at || item.timestamp).toLocaleString(),
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      setHtmlCaptures(replicas);
    } catch (err) {
      console.error("Failed to fetch replicas:", err);
      setError(err.message);
      toast.error("Failed to load replicas");
    } finally {
      setFetchingCaptures(false);
      if (isInitial) setInitialLoading(false);
      toast.dismiss("refresh");
    }
  };

  useEffect(() => {
    fetchCaptures(true);
    //   const interval = setInterval(() => fetchCaptures(false), 10000);
    //   return () => clearInterval(interval);
  }, []);

  if (initialLoading) {
    return (
      <div className="!flex !flex-col !items-center !justify-center !h-screen !text-gray-600">
        <Loader2 className="!w-8 !h-8 !animate-spin !mb-3" />
        <p className="!text-base">Loading HTML replicas...</p>
      </div>
    );
  }

  return (
    <div className="!p-6 mx-auto">
      {/* --- Form --- */}
      <form onSubmit={handleSubmit} className="bg-[#ff850f] !px-4 !py-4 rounded-xl !space-y-4">
        <input type="text" placeholder="Enter website URL" value={url} onChange={(e) => setUrl(e.target.value)} className="!w-full !p-3 !border !border-gray-300 !rounded-lg !focus:ring-2 !focus:ring-blue-500 !focus:border-blue-500 !outline-none" />

        <textarea placeholder="Optional custom instructions..." value={customInstructions} onChange={(e) => setCustomInstructions(e.target.value)} className="!w-full !p-3 !border !border-gray-300 !rounded-lg !focus:ring-2 !focus:ring-blue-500 !focus:border-blue-500 !outline-none !h-28 !resize-none bg-white" />
        <div className="flex justify-center item-center">
          <button type="submit" disabled={loading || !url.trim()} className="w-full !max-w-xs text-white cursor-pointer text-xl !px-6 !py-2 rounded-sm !bg-[#008080] hover:!bg-[#007070] text-center flex justify-center item-center mx-auto">
            {loading ? <Loader2 className="!w-5 !h-5 !animate-spin" /> : "Generate Raplica"}
          </button>
        </div>
      </form>

      {error && <div className="!mt-4 !p-4 !bg-red-50 !border !border-red-200 !rounded-lg !text-red-700">Error: {error}</div>}

      {/* --- Replica List --- */}
      <div className="!mt-10">
        <div className="!flex !items-center !justify-between !mb-3">
          <h2 className="!text-xl !font-semibold !text-gray-800">HTML Replicas</h2>
          <div className="!flex !items-center !gap-2">
            {fetchingCaptures && (
              <span className="!flex !items-center !gap-1 !text-sm !text-gray-500">
                <Loader2 className="!w-4 !h-4 !animate-spin" /> Updating...
              </span>
            )}
            <button
              onClick={() => {
                toast.loading("Refreshing...", { toastId: "refresh" });
                fetchCaptures().then(() => {
                  toast.dismiss("refresh");
                  toast.success("Replicas updated!");
                });
              }}
              className="!p-1 !rounded !text-gray-500 hover:!text-blue-600 cursor-pointer"
              title="Refresh Now"
            >
              <RefreshCcw className="!w-4 !h-4" />
            </button>
          </div>
        </div>

        {!fetchingCaptures && htmlCaptures.length === 0 && <p className="!text-gray-500">No replicas found.</p>}

        <div className="!grid md:!grid-cols-2 lg:!grid-cols-3 !gap-4">
          {htmlCaptures.map((cap) => (
            <div key={cap.id} className="!bg-white !border !rounded-xl !shadow-sm hover:!shadow-md !transition-all !overflow-hidden text-left">
              <div className="!p-4">
                {/* <p className="!text-sm !font-medium !text-gray-800 !mb-1">ID: {cap.id}</p> */}
                <p className="!text-xs !text-gray-500 !mb-2">{cap.date}</p>
                <p className="!text-xs !text-gray-600 !mb-3 !font-mono !break-all">{cap.url}</p>

                <div className="!flex !gap-3 !mb-2">
                  {cap.html_path && (
                    <a href={cap.html_path} target="_blank" rel="noopener noreferrer" className="!inline-block !bg-[#008080] hover:!bg-[#007070] !text-white !px-4 !py-2 !rounded-lg !text-sm !transition-colors">
                      View HTML
                    </a>
                  )}
                  {cap.screenshot_path && (
                    <a href={cap.screenshot_path} target="_blank" rel="noopener noreferrer" className="!inline-block !bg-gray-200 !text-gray-800 !px-4 !py-2 !rounded-lg !text-sm hover:!bg-gray-300 !transition-colors">
                      Screenshot
                    </a>
                  )}
                </div>

                {/* --- Collapsible custom details --- */}
                <div className="!bg-gray-50 !rounded-lg !text-sm !text-gray-700 !overflow-auto">
                  <button onClick={() => setHtmlCaptures((prev) => prev.map((r) => (r.id === cap.id ? { ...r, openDetails: !r.openDetails } : r)))} className="!w-full !flex !items-center !justify-between !p-2 !font-medium !text-gray-800 hover:!bg-gray-100 !transition-colors cursor-pointer">
                    <span>Details</span>
                    <ChevronDown className={`!w-4 !h-4 !transform !transition-transform ${cap.openDetails ? "!rotate-180" : ""}`} />
                  </button>

                  <div className={`!transition-all !duration-300 !ease-in-out !overflow-auto ${cap.openDetails ? "!max-h-60 !opacity-100" : "!max-h-0 !opacity-0"}`}>
                    <div className="!p-2 !border-t !border-gray-200 !whitespace-pre-line text-left">
                      <p>
                        <span className="!font-semibold">Status:</span> {cap.status}
                      </p>
                      <p>
                        <span className="!font-semibold">Custom Instructions:</span> {cap.custom_instructions || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Test;
